<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'codigoContratoVenda', label: 'Código Contrato', width: 130, sortable: true, slotName: 'codigoContrato' },
  { field: 'nomeCliente', label: 'Cliente', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'numeroContratoCliente', label: 'Número Contrato Cliente', sortable: true, class: 'text-body-secondary' },
  { field: 'dataContrato', label: 'Data', align: 'center', sortable: true, slotName: 'dataContrato' },
  { field: 'lstMoeda', label: 'Moeda', align: 'center', sortable: true, slotName: 'lstMoeda' },
  { field: 'valorTotalContrato', label: 'Valor Total', align: 'right', class: 'fw-bold', sortable: true, formatter: (v, row) => formatarMoeda(v, row.lstMoeda) },
  { field: 'lstStatusContratoVenda', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstMoeda: (row) => getMoedaIso(row.lstMoeda),
    lstStatusContratoVenda: (row) => getStatusTexto(row.lstStatusContratoVenda)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({})

const filtros = reactive({
  busca: '',
  lstStatus: ''
})

const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('CONTRATOS_VENDA_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('VENDAS').some(p =>
    p === 'COMPLETO' || (p.includes('CONTRATOS_VENDA') && p.includes('COMPLETO'))
  )
})
const temPermissaoVisualizacao = computed(() => {
  if (authStore.pode('CONTRATOS_VENDA_VISUALIZACAO')) return true
  if (authStore.getPermsDoModulo('VENDAS').some(p => p.includes('VISUALIZACAO'))) return true
  return false
})
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ContratoVenda'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)

    const response = await api.get(`/ContratosVenda?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os contratos de venda.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatus = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/ContratosVenda/${item.idContratoVenda}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados do contrato.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idContratoVenda
  auditoriaSelecionada.nome = item.numeroContratoCliente
  mostrarAuditoria.value = true
}

const getMoedaIso = (idMoeda) => {
  if (!idMoeda) return 'BRL'
  const moedaObj = (listas.LstMoeda || []).find(m => m.idListaClienteItem === idMoeda || m.textoLista === idMoeda)
  const texto = moedaObj ? moedaObj.textoLista : idMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : 'BRL'
}

const formatarMoeda = (valor, idMoeda) => {
  if (valor == null) return '-'
  const isoCode = getMoedaIso(idMoeda)
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: isoCode }).format(valor)
  } catch (e) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
  }
}

const getStatusTexto = (idStatus) => {
  const statusObj = (listas.LstStatusContratoVenda || []).find(s => s.idListaClienteItem === idStatus)
  return statusObj ? statusObj.textoLista : idStatus
}

const obterCorStatus = (idStatus) => {
  const texto = String(getStatusTexto(idStatus)).toUpperCase()

  if (texto.includes('ATIVO') || texto.includes('APROVADO') || texto.includes('CONCLUÍDO') || texto.includes('FINALIZADO') || texto.includes('ENCERRADO')) {
    return 'badge-ghost-success'
  }
  if (texto.includes('ABERTO') || texto.includes('PENDENTE') || texto.includes('DIGITAÇÃO')) {
    return 'badge-ghost-warning'
  }
  if (texto.includes('CANCELADO') || texto.includes('INATIVO') || texto.includes('REPROVADO')) {
    return 'badge-ghost-danger'
  }
  if (texto.includes('ANÁLISE') || texto.includes('ENVIADO') || texto.includes('CONFIRMADO')) {
    return 'badge-ghost-info'
  }
  if (texto.includes('PROCESSANDO')) {
    return 'badge-ghost-primary'
  }

  return 'badge-ghost-secondary'
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <div class="contrato-venda-administrar-wrapper">
  <PaginaAdministrar
    titulo="Gestão de Contratos de Venda"
    subtitulo="Administração de contratos, incoterms e valores acordados com clientes"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Número do Contrato</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar por número..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in (listas.LstStatusContratoVenda || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-success" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-success"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum contrato de venda encontrado."
      row-key="idContratoVenda"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #codigoContrato="{ row }">
        <span class="text-muted fw-medium">{{ row.codigoContratoVenda || '-' }}</span>
      </template>

      <template #dataContrato="{ row }">
        {{ formatarDataBR(row.dataContrato) }}
      </template>

      <template #lstMoeda="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getMoedaIso(row.lstMoeda) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusContratoVenda)">{{ getStatusTexto(row.lstStatusContratoVenda) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-success' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ContratoVenda"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    :tabelas-filhas="['ContratoVendaProduto', 'ContratoVendaCondicaoPg']"
    chave-estrangeira="IdContratoVenda"
    @fechar="mostrarAuditoria = false"
  />
</div>
</template>

<style scoped>
/* === Tema Verde Escuro ContratoVenda === */
.contrato-venda-administrar-wrapper {
  --ct-green: #0f5c3a;
  --ct-green-bg: #0b4a2e;
  --ct-green-border: #1a7a4a;
  --ct-green-text: #ffffff;
}

[data-bs-theme="dark"] .contrato-venda-administrar-wrapper {
  --ct-green: #1a8a4e;
  --ct-green-bg: #0d3d25;
  --ct-green-border: #2a9a5e;
  --ct-green-text: #a3cfb4;
}
</style>
